// Reads the commercial shipping price spreadsheet (saved as CSV) into price configurations per
// zone, and writes an empty template in the same layout. Nothing here talks to the API: the result
// only fills the tables on screen, which are saved through the normal configuration flow.

import { CSV_FORMATS } from '../../utils/csvUtils'

const EXCESS_ROW = /adicional|excedente|excess|extra/i
// The extended zone surcharge is one flat price per zone, not a weight range.
const EXTENDED_ZONE_ROW = /zona est|zona ext|extended/i
const BR_COLUMN = /^([A-Z]{2})[-_ ](CAP|INT)$/

const normalizeHeader = header => (header || '').toString().trim().toUpperCase()

// "SP-CAP" is how the commercial spreadsheet names the Ground zones of Brazil; any zone code of the
// country is accepted as well.
export const zoneForColumn = (header, zones) => {
  const normalized = normalizeHeader(header)
  if (zones.includes(normalized)) return normalized

  const match = normalized.match(BR_COLUMN)
  const groundZone = match && `GROUND_${match[1]}_${match[2]}`
  return groundZone && zones.includes(groundZone) ? groundZone : null
}

// A lone separator followed by groups of three digits ("1.011", "1,250") is a thousands separator in
// the country's convention: Brazil writes "1.011" for one thousand and eleven, Mexico "1,011".
const THOUSANDS_ONLY = /^[1-9]\d{0,2}([.,])\d{3}(\1\d{3})*$/

// Accepts "R$ 1.011,15", "$1,011.15", "10,10" and "10.10". With both separators the last one is the
// decimal point; a single one is decimal unless it groups thousands the way the country writes them.
// Used for the weights; prices go through parsePrice, which rejects what it cannot tell apart.
export const parseNumber = (value, country) => {
  const text = (value ?? '').toString().replace(/R?\$/i, '').replace(/\s/g, '')
  if (text === '') return null

  const thousandsSeparator = country === 'BR' ? '.' : ','
  const lastSeparator = Math.max(text.lastIndexOf(','), text.lastIndexOf('.'))
  const hasBoth = text.includes(',') && text.includes('.')
  const thousandsMatch = text.match(THOUSANDS_ONLY)

  let normalized = text
  if (hasBoth) {
    normalized = `${text.slice(0, lastSeparator).replace(/[.,]/g, '')}.${text.slice(lastSeparator + 1)}`
  } else if (thousandsMatch && thousandsMatch[1] === thousandsSeparator) {
    normalized = text.replace(/[.,]/g, '')
  } else {
    normalized = text.replace(',', '.')
  }
  const number = Number(normalized)
  return Number.isFinite(number) ? number : null
}

// Prices are read by the warehouse country's format. "1.200,50" and "1,200.50" read alike anywhere;
// a lone thousands separator followed by three digits ("1.200" in Brazil, "1,200" in Mexico) could
// be a whole price or one with three decimals, so it is rejected instead of guessed. The export
// never writes a thousands separator, so an exported file always reads back.
export const parsePrice = (value, country) => {
  const format = CSV_FORMATS[country]
  const text = (value ?? '').toString().replace(/R?\$/i, '').replace(/\s/g, '')
  if (!format || !/^\d[\d.,]*$/.test(text)) return null

  const separators = text.replace(/\d/g, '')
  let integer = text
  let fraction = ''

  if (separators.includes('.') && separators.includes(',')) {
    const lastSeparator = Math.max(text.lastIndexOf('.'), text.lastIndexOf(','))
    integer = text.slice(0, lastSeparator)
    fraction = text.slice(lastSeparator + 1)
  } else if (separators.length === 1) {
    const [head, tail] = text.split(separators)
    if (separators !== format.decimal && tail.length === 3) return null
    integer = head
    fraction = tail
  }

  if (!/^\d+$|^\d{1,3}([.,]\d{3})+$/.test(integer) || /[.,]/.test(fraction)) return null
  return Number(`${integer.replace(/[.,]/g, '')}${fraction ? `.${fraction}` : ''}`)
}

const isBlank = value => (value ?? '').toString().trim() === ''

export const parseShippingPriceRows = (rows, zones, country) => {
  const number = value => parseNumber(value, country)
  const readPrice = value => parsePrice(value, country)
  // A blank "weight max" is an open ended last range ("2 kg and up").
  const isRangeRow = row => number(row[0]) !== null && (number(row[1]) !== null || isBlank(row[1]))
  const rangeEnd = row => (isBlank(row[1]) ? Infinity : number(row[1]))

  const headerIndex = rows.findIndex(row => row.some(cell => zoneForColumn(cell, zones)))
  if (headerIndex === -1) return { error: 'no_zone_columns' }

  const header = rows[headerIndex]
  const body = rows.slice(headerIndex + 1)
  const rangeRows = body.filter(isRangeRow)
  if (rangeRows.length === 0) return { error: 'no_weight_ranges' }

  // A row with prices whose weight does not read ("1 kg", or text between two ranges) would be
  // dropped and its range absorbed by the next one at the wrong price. Other rows with prices (ADV,
  // GRIS, deadline) and rows without prices stay ignored.
  const firstRange = body.indexOf(rangeRows[0])
  const lastRange = body.indexOf(rangeRows[rangeRows.length - 1])
  const isLabeledRow = row => EXCESS_ROW.test((row[0] || '').toString()) || EXTENDED_ZONE_ROW.test((row[0] || '').toString())
  const unreadableRow = body.find((row, index) => {
    if (isLabeledRow(row)) return false
    const looksLikeRange = /^\d/.test((row[0] ?? '').toString().trim()) || (index > firstRange && index < lastRange)
    return looksLikeRange && !isRangeRow(row) && row.slice(2).some(cell => !isBlank(cell))
  })
  if (unreadableRow) return { error: 'unreadable_weight_row', row: rows.indexOf(unreadableRow) + 1 }

  // Ranges only go up: one ending at or before the previous one would be an invalid table.
  const unordered = rangeRows.findIndex((row, index) => {
    const previousEnd = index === 0 ? 0 : rangeEnd(rangeRows[index - 1])
    return rangeEnd(row) <= previousEnd || previousEnd === Infinity
  })
  if (unordered !== -1) return { error: 'unordered_weight_ranges', row: rows.indexOf(rangeRows[unordered]) + 1 }

  const excessRow = body.find(row => EXCESS_ROW.test((row[0] || '').toString()))
  const extendedZoneRow = body.find(row => EXTENDED_ZONE_ROW.test((row[0] || '').toString()))
  // An extra kg row left blank everywhere (as in the template) is not in use.
  const usesExcessRow = Boolean(excessRow) && excessRow.slice(2).some(cell => !isBlank(cell))
  const extendedZonePrices = {}
  const ignoredColumns = []
  const invalidZones = []
  const invalidExtendedZones = []
  const prices = {}

  header.forEach((cell, columnIndex) => {
    if (columnIndex < 2 || normalizeHeader(cell) === '') return

    const zone = zoneForColumn(cell, zones)
    if (!zone) {
      ignoredColumns.push(cell.toString().trim())
      return
    }

    const rangePrices = rangeRows.map(row => readPrice(row[columnIndex]))
    const excessPrice = usesExcessRow ? readPrice(excessRow[columnIndex]) : null
    const isOpenEnded = isBlank(rangeRows[rangeRows.length - 1][1])
    // With the extra kg row in use, a zone must have exactly one way to bill beyond the last range:
    // a missing extra kg price on a closed last range would bill heavier parcels at the flat price,
    // and one on an open last range would be dropped.
    const hasExcessConflict = usesExcessRow && (excessPrice === null ? !isOpenEnded || !isBlank(excessRow[columnIndex]) : isOpenEnded)
    if (rangePrices.some(price => price === null) || hasExcessConflict) {
      invalidZones.push(zone)
      return
    }

    // Ranges are contiguous from 0: each starts where the previous ended, whatever the sheet's
    // "weight min" says (0.251 after 0.25 is the same boundary written for humans).
    const ranges = rangeRows.map((row, index) => ({
      from: index === 0 ? 0 : number(rangeRows[index - 1][1]),
      to: isBlank(row[1]) ? null : number(row[1]),
      price: rangePrices[index],
      price_mode: 'TOTAL',
      excess: false,
      range_exclusion: null,
    }))

    if (excessPrice !== null) {
      ranges.push({ from: ranges[ranges.length - 1].to, to: null, price: excessPrice, price_mode: 'PER_UNIT', excess: true, range_exclusion: null })
    } else {
      ranges[ranges.length - 1].to = null
    }

    prices[zone] = ranges
  })

  // A blank cell leaves the zone's surcharge as it is; one that does not read is named in the
  // summary, since the zone keeps its previous surcharge.
  if (extendedZoneRow) {
    header.forEach((cell, columnIndex) => {
      const zone = columnIndex >= 2 && zoneForColumn(cell, zones)
      if (!zone || isBlank(extendedZoneRow[columnIndex])) return

      const extendedZonePrice = readPrice(extendedZoneRow[columnIndex])
      if (extendedZonePrice === null) invalidExtendedZones.push(zone)
      else extendedZonePrices[zone] = extendedZonePrice
    })
  }

  return { prices, ignoredColumns, invalidZones, invalidExtendedZones, extendedZonePrices, hasExtendedZoneRow: Boolean(extendedZoneRow) }
}

const delimiterFor = country => CSV_FORMATS[country]?.delimiter ?? ';'

const columnForZone = (zone, country) => {
  const match = country === 'BR' && zone.match(/^GROUND_([A-Z]{2})_(CAP|INT)$/)
  return match ? `${match[1]}-${match[2]}` : zone
}

// excessLabel is written in the user's language; any wording with "adicional", "excedente",
// "excess" or "extra" reads back.
export const buildShippingPriceTemplate = (zones, country, excessLabel) => {
  const empty = zones.map(() => '')
  return [
    ['Weight min', 'Weight max', ...zones.map(zone => columnForZone(zone, country))],
    ['0', '', ...empty],
    ['', '', ...empty],
    [excessLabel, '', ...empty],
  ]
    .map(row => row.join(delimiterFor(country)))
    .join('\n')
}

const formatNumber = (value, country) => {
  if (value === null || value === undefined || value === '') return ''
  const text = String(Number(value))
  return CSV_FORMATS[country]?.decimal === ',' ? text.replace('.', ',') : text
}

const rangesSignature = priceConfiguration => {
  const standard = priceConfiguration.filter(range => !range.excess).map(range => range.to ?? 'open')
  return `${standard.join(',')}|${priceConfiguration.some(range => range.excess)}`
}

// The open tab's current prices in the import layout, so a file exported and imported unchanged
// changes nothing. A CSV holds one set of weight ranges: the one most zones share; a zone with
// other ranges is left blank (and skipped if imported back).
export const buildShippingPriceExport = ({ zones, country, excessLabel, priceConfigurationFor, extendedZoneLabel = null, extendedZonePriceFor = null }) => {
  const configs = Object.fromEntries(zones.map(zone => [zone, priceConfigurationFor(zone)]).filter(([, config]) => config?.length > 0))
  const signatures = Object.values(configs).map(rangesSignature)
  if (signatures.length === 0) return buildShippingPriceTemplate(zones, country, excessLabel)

  const counts = signatures.reduce((acc, signature) => ({ ...acc, [signature]: (acc[signature] || 0) + 1 }), {})
  const reference = Object.keys(counts).sort((a, b) => counts[b] - counts[a])[0]
  const referenceConfig = Object.values(configs).find(config => rangesSignature(config) === reference)
  const standardRanges = referenceConfig.filter(range => !range.excess)
  const sameRanges = zone => configs[zone] && rangesSignature(configs[zone]) === reference

  const priceAt = (zone, index) => (sameRanges(zone) ? formatNumber(configs[zone].filter(range => !range.excess)[index].price, country) : '')
  const excessPrice = zone => (sameRanges(zone) ? formatNumber(configs[zone].find(range => range.excess)?.price, country) : '')

  const rows = [
    ['Weight min', 'Weight max', ...zones.map(zone => columnForZone(zone, country))],
    ...standardRanges.map((range, index) => [formatNumber(range.from, country), formatNumber(range.to, country), ...zones.map(zone => priceAt(zone, index))]),
  ]
  if (referenceConfig.some(range => range.excess)) {
    rows.push([excessLabel, '', ...zones.map(excessPrice)])
  }
  // A store with no extended zone surcharge anywhere gets no row to fill in by mistake.
  const extendedZonePrices = extendedZonePriceFor ? zones.map(zone => extendedZonePriceFor(zone)) : []
  if (extendedZonePrices.some(price => price !== null && price !== undefined)) {
    rows.push([extendedZoneLabel, '', ...extendedZonePrices.map(price => formatNumber(price, country))])
  }

  return rows.map(row => row.join(delimiterFor(country))).join('\n')
}
